// src/lib/supabase/queries/feed-queries.ts
/**
 * .tuMetrics {"tu1": 0.87, "tu2": 0.88, "tu3": 3, "notes": "Well-typed query layer with explicit local row types compensating for missing generated DB types.", "tuVersion": "2.5"}
 * .help "Supabase query helpers for feed events: DB row mapping plus fetch, insert, update and delete operations for the activity feed."
 */
import { supabase } from '../../supabase';
import { FeedEvent } from '../../../types';

// July 2026: types/supabase.ts (generated Database types) is an empty file, so
// the old `Database['public']['Tables']['feed_events']` lookups never type-
// checked. Hand-written row shape until codegen is wired up.
type Json = string | number | boolean | null | { [key: string]: Json } | Json[];

export interface FeedEventRow {
  id: string;
  created_at: string;
  type: string;
  title: string;
  description: string;
  user_id: string | null;
  initiator_id: string | null;
  card_id: string | null;
  comment_id: string | null;
  sprint_id: string | null;
  roadmap_id: string | null;
  plan_id: string | null;
  requirement_id?: string | null;
  project_id?: string | null;
  read: Record<string, boolean>;
  github_data: Json | null;
  stream_type: string | null;
  recipient_stream_id: string | null;
  shared_item_id: string | null;
  shared_item_details: Json | null;
  company_id: string | null;
  metadata: Json | null;
  user_name?: string | null;
  initiator_name?: string | null;
}

type FeedEventInsert = Partial<Omit<FeedEventRow, 'id' | 'created_at' | 'read'>> & {
  type: string;
  title: string;
  description: string;
  read: Record<string, boolean>;
};

type FeedEventUpdate = Partial<Omit<FeedEventRow, 'id' | 'created_at'>>;

// Helper function to safely parse JSONB data
const safeParseJsonb = <T>(jsonbData: string | Json | null): T | undefined => {
  if (jsonbData === null || jsonbData === undefined) {
    return undefined;
  }
  try {
    return (typeof jsonbData === 'string' ? JSON.parse(jsonbData) : jsonbData) as T;
  } catch (e) {
    console.error("Error parsing JSONB data:", e, "Data:", jsonbData);
    return undefined;
  }
};

export const mapFeedEventFromDB = (row: FeedEventRow): FeedEvent => ({
  id: row.id,
  type: row.type as FeedEvent['type'],
  title: row.title,
  description: row.description,
  createdAt: row.created_at,
  userId: row.user_id ?? undefined,
  initiatorId: row.initiator_id ?? undefined,
  cardId: row.card_id ?? undefined,
  commentId: row.comment_id ?? undefined,
  sprintId: row.sprint_id ?? undefined,
  roadmapId: row.roadmap_id ?? undefined,
  planId: row.plan_id ?? undefined,
  requirementId: row.requirement_id ?? undefined,
  projectId: row.project_id ?? undefined,
  read: row.read,
  githubData: safeParseJsonb(row.github_data),
  streamType: row.stream_type as FeedEvent['streamType'] ?? undefined,
  recipientStreamId: row.recipient_stream_id ?? undefined,
  shared_item_id: row.shared_item_id ?? undefined,
  sharedItemDetails: safeParseJsonb(row.shared_item_details),
  companyId: row.company_id ?? undefined,
  metadata: safeParseJsonb(row.metadata),
  userName: row.user_name ?? undefined,
  initiatorName: row.initiator_name ?? undefined,
});

export const mapFeedEventToDB = (event: Omit<FeedEvent, 'id' | 'createdAt' | 'read'>): FeedEventInsert => ({
  type: event.type,
  title: event.title,
  description: event.description,
  user_id: event.userId || null,
  initiator_id: event.initiatorId || null,
  card_id: event.cardId || null,
  comment_id: event.commentId || null,
  sprint_id: event.sprintId || null,
  roadmap_id: event.roadmapId || null,
  plan_id: event.planId || null,
  requirement_id: event.requirementId || null,
  project_id: event.projectId || null,
  read: {},
  github_data: event.githubData ? JSON.stringify(event.githubData) : null,
  stream_type: event.streamType || null,
  recipient_stream_id: event.recipientStreamId || null,
  shared_item_id: event.shared_item_id || null,
  shared_item_details: event.sharedItemDetails ? JSON.stringify(event.sharedItemDetails) : null,
  company_id: event.companyId || null,
});

export const mapFeedEventUpdatesToDB = (updates: Partial<FeedEvent>): FeedEventUpdate => {
  const dbUpdates: FeedEventUpdate = {};
  if (updates.type !== undefined) dbUpdates.type = updates.type;
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.userId !== undefined) dbUpdates.user_id = updates.userId;
  if (updates.initiatorId !== undefined) dbUpdates.initiator_id = updates.initiatorId;
  if (updates.cardId !== undefined) dbUpdates.card_id = updates.cardId;
  if (updates.commentId !== undefined) dbUpdates.comment_id = updates.commentId;
  if (updates.sprintId !== undefined) dbUpdates.sprint_id = updates.sprintId;
  if (updates.roadmapId !== undefined) dbUpdates.roadmap_id = updates.roadmapId;
  if (updates.planId !== undefined) dbUpdates.plan_id = updates.planId;
  if (updates.requirementId !== undefined) dbUpdates.requirement_id = updates.requirementId;
  if (updates.projectId !== undefined) dbUpdates.project_id = updates.projectId;
  if (updates.read !== undefined) dbUpdates.read = updates.read;
  if (updates.githubData !== undefined) dbUpdates.github_data = updates.githubData ? JSON.stringify(updates.githubData) : null;
  if (updates.streamType !== undefined) dbUpdates.stream_type = updates.streamType;
  if (updates.recipientStreamId !== undefined) dbUpdates.recipient_stream_id = updates.recipientStreamId;
  if (updates.shared_item_id !== undefined) dbUpdates.shared_item_id = updates.shared_item_id;
  if (updates.sharedItemDetails !== undefined) dbUpdates.shared_item_details = updates.sharedItemDetails ? JSON.stringify(updates.sharedItemDetails) : null;
  if (updates.companyId !== undefined) dbUpdates.company_id = updates.companyId;
  return dbUpdates;
};

// --- Supabase Interaction Functions ---

export const fetchFeedEvents = async (companyId: string, userId: string) => {
  const { data, error } = await supabase
    .from('feed_events')
    .select('*')
    .eq('company_id', companyId)
    .or(`user_id.eq.${userId},initiator_id.eq.${userId},user_id.is.null`)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data.map(mapFeedEventFromDB);
};

export const insertFeedEvent = async (eventData: FeedEventInsert) => {
  const { data, error } = await supabase
    .from('feed_events')
    .insert(eventData)
    .select()
    .single();

  if (error) throw error;
  return mapFeedEventFromDB(data);
};

export const updateFeedEventById = async (eventId: string, updates: FeedEventUpdate) => {
  const { data, error } = await supabase
    .from('feed_events')
    .update(updates)
    .eq('id', eventId)
    .select()
    .single();

  if (error) throw error;
  return mapFeedEventFromDB(data);
};

export const deleteFeedEventById = async (eventId: string) => {
  const { error } = await supabase
    .from('feed_events')
    .delete()
    .eq('id', eventId);

  if (error) throw error;
  return true;
};

export const fetchLastAnnouncement = async (companyId: string): Promise<string | null> => {
  const { data, error } = await supabase
    .from('feed_events')
    .select('description')
    .eq('company_id', companyId)
    .ilike('description', '%#announcement%')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching last announcement:', error);
    return null;
  }

  return data?.description || null;
};
